import {events}from'@locmod/event-aggregator';import React,{useState,useEffect,useRef}from'react';const modalsRegistry = {};
class ModalManager {
    constructor() {
        this.events = events;
        this.state = new Map();
        this.standaloneModals = {};
        this.lastId = 0;
        this.eventName = 'locmod-modal-change';
    }
    generateId() {
        return this.lastId++;
    }
    subscribe(listener) {
        this.events.subscribe(this.eventName, listener);
    }
    unsubscribe(listener) {
        this.events.unsubscribe(this.eventName, listener);
    }
    emitRender(name) {
        this.events.dispatch(this.eventName, name);
    }
    openModal(name, props) {
        const id = this.generateId();
        const isStandalone = this.standaloneModals[name] > 0;
        const closeModal = (withOnClose) => {
            this.state.delete(id);
            this.emitRender(name);
            if (withOnClose === true && typeof props?.onClose === 'function') {
                props?.onClose();
            }
        };
        this.state.set(id, { name, props, closeModal, isStandalone });
        this.emitRender(name);
        return closeModal;
    }
    // closes every modal with requested name
    closeModal(name) {
        this.state.forEach((value, key) => {
            if (value.name === name) {
                this.state.delete(key);
            }
        });
        this.emitRender(name);
    }
    getState() {
        return this.state;
    }
    markAsStandalone(name) {
        this.standaloneModals[name] = (this.standaloneModals[name] || 0) + 1;
    }
    unmarkAsStandalone(name) {
        this.standaloneModals[name] = (this.standaloneModals[name] || 1) - 1;
    }
    getOpenModalNames() {
        const openModalNames = [];
        this.getState().forEach((value) => {
            openModalNames.push(value.name);
        });
        return openModalNames;
    }
}
const manager = new ModalManager();
const openModal = (name, ...args) => manager.openModal(name, args[0]);
const closeModal = (name) => manager.closeModal(name);
const getOpenModalNames = () => manager.getOpenModalNames();
const registerModals = (registry) => {
    Object.keys(registry).forEach((key) => {
        modalsRegistry[key] = registry[key];
    });
    // unregister function
    return () => {
        Object.keys(registry).forEach((key) => {
            delete modalsRegistry[key];
        });
    };
};const ModalsRenderer = ({ registry }) => {
    const [_, update] = useState(0);
    useEffect(() => {
        if (registry) {
            return registerModals(registry);
        }
    }, [registry]);
    useEffect(() => {
        const listener = () => {
            update((value) => value + 1);
        };
        manager.subscribe(listener);
        return () => {
            manager.unsubscribe(listener);
        };
    }, []);
    const nodes = [];
    for (const [id, { name, props, closeModal, isStandalone }] of manager.getState().entries()) {
        // skip standalone modals, they render themselves
        if (isStandalone) {
            continue;
        }
        if (!modalsRegistry[name]) {
            continue;
        }
        const Component = modalsRegistry[name];
        nodes.push(React.createElement(Component, { key: id, name: name, closeModal: closeModal, ...props }));
    }
    return (React.createElement(React.Fragment, null, nodes));
};// to render a modal in-place without registration required
const standaloneModal = (modalName, Component) => {
    // ATTN because we can render several instances of standaloneModal,
    //  we should track them and render only in the active one
    let lastId = 1;
    let activeIds = [];
    const Wrapper = () => {
        const [, forceUpdate] = useState(0);
        const instanceIdRef = useRef(lastId++);
        useEffect(() => {
            const listener = () => {
                forceUpdate((value) => (value + 1));
            };
            manager.subscribe(listener);
            manager.markAsStandalone(modalName);
            activeIds.push(instanceIdRef.current);
            return () => {
                activeIds = activeIds.filter((value) => value !== instanceIdRef.current);
                manager.unsubscribe(listener);
                manager.unmarkAsStandalone(modalName);
            };
        }, []);
        // ATTN show only if this instance is the first instance of this modal
        if (activeIds[0] !== instanceIdRef.current) {
            return null;
        }
        let nodes = [];
        for (const [id, { name, props, closeModal }] of manager.getState().entries()) {
            if (modalName === name) {
                nodes.push(React.createElement(Component, { key: id, name: name, closeModal: closeModal, ...props }));
            }
        }
        return (React.createElement(React.Fragment, null, nodes));
    };
    Wrapper.displayName = `StandaloneModal(${Component.displayName || Component.name || modalName})`;
    return Wrapper;
};function useModalOpenState(name) {
    const [isOpen, setIsOpen] = useState(false);
    useEffect(() => {
        const listener = (eventModalName) => {
            if (eventModalName !== name) {
                return;
            }
            // we take info if modal is open from the manager state (getOpenModalNames), not from event
            // because:
            //  - we can have several open modals with the same name
            //  - we can close all of them (manager.closeModal) or just one of them (by method closeModal in each modal instance)
            setIsOpen(manager.getOpenModalNames().includes(name));
        };
        manager.subscribe(listener);
        return () => {
            manager.unsubscribe(listener);
        };
    }, [name]);
    return isOpen;
}export{ModalsRenderer,closeModal,getOpenModalNames,openModal,registerModals,standaloneModal,useModalOpenState};